import React, { Component } from "react";
import { AxiosResponse } from "axios";
import { Link } from "react-router-dom";
import InfiniteScroll from "react-infinite-scroll-component";
import { getRequest } from "../../utils/httpRequest";
import { BlogPostCard } from "./types";

interface Author {
  name: string;
  slug: string;
  bio: string | null;
  photo_url: string | null;
}

interface Props {
  match: { params: { slug: string } };
}

interface State {
  author?: Author;
  posts: BlogPostCard[];
  page: number;
  hasMore: boolean;
  loading: boolean;
  searching: boolean;
  query: string;
  notFound: boolean;
  failed: boolean;
}

// Everything an author's page needs and nothing else: who wrote them, and the
// posts. Deliberately built from the same blog-page classes as the index rather
// than a set of its own, so it inherits the blog's look and any later change to
// it — a second stylesheet saying nearly the same thing is how two pages drift
// apart.
class AuthorPage extends Component<Props, State> {
  private searchTimer?: number;
  private searchToken = 0;
  private unmounted = false;

  state: State = {
    posts: [],
    page: 1,
    hasMore: false,
    loading: true,
    searching: false,
    query: "",
    notFound: false,
    failed: false,
  };

  componentDidMount() {
    this.fetchPosts(1, "");
  }

  componentWillUnmount() {
    this.unmounted = true;
    window.clearTimeout(this.searchTimer);
  }

  get slug() {
    return this.props.match.params.slug;
  }

  fetchPosts = (page: number, query: string, token = ++this.searchToken) => {
    const params = new URLSearchParams({
      author: this.slug,
      page: String(page),
    });
    if (query.trim()) params.append("q", query.trim());

    getRequest(`/api/blog-posts.json?${params.toString()}`)
      .then((response: AxiosResponse<any>) => {
        // A later keystroke has already been sent; this answer is stale.
        if (this.unmounted || token !== this.searchToken) return;

        const { author, posts, pagination } = response.data;

        this.setState((state) => ({
          author: author || state.author,
          // Only a successful answer can say the writer does not exist.
          notFound: !author,
          failed: false,
          posts: page === 1 ? posts : [...state.posts, ...posts],
          page: pagination.current_page,
          hasMore: !!pagination.next_page,
          loading: false,
          searching: false,
        }));
      })
      .catch(() => {
        if (this.unmounted) return;

        // A dropped request is not the same as a writer who does not exist.
        // Saying "we can't find that writer" because page three failed would
        // throw away a page that had loaded perfectly well.
        this.setState({ loading: false, searching: false, failed: true });
      });
  };

  fetchMorePosts = () => this.fetchPosts(this.state.page + 1, this.state.query, this.searchToken);

  handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value;
    // Bumped here rather than when the timer fires: in the gap between the two,
    // an answer to the previous query would otherwise land and be shown as
    // though it belonged to this one.
    this.searchToken += 1;
    this.setState({ query, searching: true });

    window.clearTimeout(this.searchTimer);
    this.searchTimer = window.setTimeout(() => this.fetchPosts(1, query), 300);
  };

  retry = () => {
    this.setState({ loading: true, failed: false });
    this.fetchPosts(1, this.state.query);
  };

  handleSearchSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    window.clearTimeout(this.searchTimer);
    this.fetchPosts(1, this.state.query);
  };

  render() {
    const { author, posts, loading, searching, query, notFound, failed } = this.state;

    if (loading) {
      return (
        <main className="blog-page">
          <div className="ui active centered inline loader blog-page__loader" />
        </main>
      );
    }

    if (failed && !author) {
      return (
        <main className="blog-page">
          <div className="blog-page__empty">
            <p>
              That didn't load.{" "}
              <button type="button" className="link-button" onClick={this.retry}>
                Try again
              </button>
              , or head <Link to="/blog">back to the blog</Link>.
            </p>
          </div>
        </main>
      );
    }

    if (notFound || !author) {
      return (
        <main className="blog-page">
          <div className="blog-page__empty">
            <p>
              We can't find that writer. <Link to="/blog">Back to the blog</Link>.
            </p>
          </div>
        </main>
      );
    }

    return (
      <div className="blog-page">
        <header className="blog-page__hero">
          <span className="blog-page__watermark" aria-hidden="true">
            42
          </span>
          <p className="blog-page__intro">
            Words by <em>{author.name}</em>.
          </p>
          {author.bio && <p className="blog-page__author-bio">{author.bio}</p>}

          <form
            className="blog-page__search"
            role="search"
            onSubmit={this.handleSearchSubmit}
          >
            <i className="search icon" aria-hidden="true" />
            <input
              type="search"
              value={query}
              onChange={this.handleSearchChange}
              placeholder={`Search ${author.name.split(" ")[0]}'s posts…`}
              aria-label={`Search posts by ${author.name}`}
            />
          </form>
        </header>

        <main className="blog-page__main">
          {posts.length === 0 ? (
            <div className="blog-page__empty">
              {searching ? (
                <div className="ui active centered inline loader blog-page__loader" />
              ) : (
                <p>
                  Nothing here
                  {query.trim() ? <> for “{query.trim()}”</> : null}.{" "}
                  <Link to="/blog">Back to the blog</Link>.
                </p>
              )}
            </div>
          ) : (
            <InfiniteScroll
              className="blog-page__scroll"
              dataLength={posts.length}
              hasMore={this.state.hasMore}
              loader={
                <div className="ui active centered inline loader blog-page__loader" />
              }
              next={this.fetchMorePosts}
            >
              <div className="blog-page__grid">
                {posts.map((post) => (
                  <article className="blog-page__card" key={post.id}>
                    <Link to={post.blog_post_path}>
                      {post.hero_image_url && (
                        <div className="blog-page__thumb">
                          <img src={post.hero_image_url} alt="" loading="lazy" />
                        </div>
                      )}
                      <div className="blog-page__card-body">
                        {post.skill_area && <em>{post.skill_area.name}</em>}
                        <span>{new Date(post.published_at).toDateString()}</span>
                        <h2>{post.title}</h2>
                        <p>{post.excerpt}</p>
                        <strong>Read post</strong>
                      </div>
                    </Link>
                  </article>
                ))}
              </div>
            </InfiniteScroll>
          )}
        </main>
      </div>
    );
  }
}

export default AuthorPage;
